import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PackageCheck } from 'lucide-react';
import type { SupplyReportLineDto } from '@eombor/contracts';
import { Badge, Button, Card, ConfirmDialog, Input, cn, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { BackButton } from '../../../shared/ui/BackButton.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { CellPicker } from '../../stock/ui/CellPicker.js';
import { LabelPrintDialog } from '../../warehouse/ui/LabelPrintDialog.js';
import { LevelSplitDialog } from '../../warehouse/ui/LevelSplitDialog.js';
import { RackFacade, type FacadePendingItem } from '../../warehouse/ui/RackFacade.js';
import { applyLevel } from '../../warehouse/model/split-bay.js';
import { warehouseApi, type LayoutRack } from '../../warehouse/api/warehouse.api.js';
import { warehouseKeys, useWarehouseLayout } from '../../warehouse/model/warehouse.queries.js';

/**
 * JOYLASHTIRISH — alohida sahifa.
 *
 * Yon panel emas, sahifa: joylashtirishning asosiy asbobi javon
 * chizmasi bo'lib, u 24 rem li tirqishga sig'maydi — kataklar shu qadar
 * kichrayadiki, ularni bosib bo'lmaydi. Shuning uchun satr bosilganda
 * hujjat jadvali o'rnini shu sahifa egallaydi va orqaga qaytish tugmasi
 * hujjatga qaytaradi.
 *
 * BIR NECHTA SATR birdan kelishi mumkin: jadvalda belgilangan satrlar
 * bitta katakka tushadi. Mayda mahsulotlar aynan shunday joylanadi — bir
 * quti bolt, gayka va shayba bitta uyaga — va har biri uchun alohida
 * ekran ochish o'sha ishni uch barobar cho'zardi.
 *
 * KATAK UCH YO'L BILAN tanlanadi va uchalasi bir vaqtda ishlaydi:
 *
 *   1. terish — kodni yozadi;
 *   2. skaner — etiketkani o'qiydi, kod o'sha maydonga tushadi;
 *   3. chizma — javon fasadida katakni bosadi.
 *
 * Uchtasi ham BITTA holatga yozadi (`code` + `cellId`), shuning uchun
 * skanerdan keyin chizmada o'sha katak yonib turadi va aksincha. Ikki
 * alohida holat bo'lganda ular bir-biriga zid bo'lib qolardi.
 */

export interface PlacementDraft {
  lineId: string;
  cellId: string;
  qty: number;
  supplier?: string;
}

export interface SupplyPlacePageProps {
  /** Joylashtiriladigan satrlar — kamida bitta. */
  lines: SupplyReportLineDto[];
  /** Sarlavhada ko'rinadi — skladchi qaysi hujjat ustida ishlayotganini bilsin. */
  docNo: string;
  warehouseId: string;
  /** Ish savati — sahifa uni CHIZADI, lekin boshqarmaydi. */
  basket?: ReactNode;
  /**
   * Savatdagi (hali yuklanmagan) joylashtirishlar — chizmada o'z
   * kataklarida ko'rinadi. Chizma server holatini chizadi va ularni
   * o'zi bilmaydi.
   */
  pending?: readonly PendingPlacement[];
  /** Katakdagi savat yozuvlarini olib tashlash — miqdor «Qolgani» ga qaytadi. */
  onRemoveFromCell?: (cellId: string) => void;
  onAdd: (drafts: PlacementDraft[]) => void;
  onBack: () => void;
}

/** Savatdagi bitta joylashtirish — chizma uchun. */
export interface PendingPlacement extends FacadePendingItem {
  cellId: string;
}

/** Satrlarning o'zligi — tanlov shu o'zgarganda tozalanadi. */
const idsOf = (lines: readonly SupplyReportLineDto[]): string =>
  lines.map((line) => line.id).join(',');

export const SupplyPlacePage = ({
  lines,
  docNo,
  warehouseId,
  basket,
  pending,
  onRemoveFromCell,
  onAdd,
  onBack,
}: SupplyPlacePageProps) => {
  const [code, setCode] = useState('');
  const [cellId, setCellId] = useState<string | null>(null);
  const [supplier, setSupplier] = useState('');
  const [rackId, setRackId] = useState<string | null>(null);
  /* Miqdorlar satr bo'yicha: bir tanlovda 20 dona bolt va 4 dona gayka
     bo'lishi mumkin. Kalit — satr `id` si. */
  const [qtyById, setQtyById] = useState<Record<string, string>>({});

  /* QATORNI O'LCHAMLARGA BO'LISH — shu yerdan, javon oldida turib.
     Mahsulot 16 sm li o'ringa sig'masligi aynan joylashtirayotganda
     bilinadi; ombor kartochkasiga qaytib borish esa boshqa ekran va
     boshqa fikr. */
  const [splitLevel, setSplitLevel] = useState<number | null>(null);
  const [printCells, setPrintCells] = useState<string[] | null>(null);

  /* × bosilgan katak — tasdiqlanguncha hech narsa o'chmaydi. */
  const [removeCellId, setRemoveCellId] = useState<string | null>(null);

  const pendingByCell = useMemo(() => {
    const map = new Map<string, FacadePendingItem[]>();
    for (const item of pending ?? []) {
      const list = map.get(item.cellId) ?? [];
      list.push({ key: item.key, name: item.name, qty: item.qty });
      map.set(item.cellId, list);
    }
    return map;
  }, [pending]);

  const qtyRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLInputElement>(null);

  const key = idsOf(lines);

  /* Tanlov ALMASHGANDA hamma narsa tozalanadi. Aks holda oldingi
     satrning katagi yangisiga o'tib ketardi va skladchi buni
     payqamasligi mumkin. */
  useEffect(() => {
    setCode('');
    setCellId(null);
    setSupplier('');
    setQtyById(Object.fromEntries(lines.map((line) => [line.id, String(line.toPlaceQty)])));
    cellRef.current?.focus();
    // `lines` har chizishda yangi massiv bo'ladi — o'zligi `key` bilan olinadi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const layout = useWarehouseLayout(warehouseId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = usePermission();

  /* Ruxsat aynan shu ombor uchun so'raladi: MTU ga berilgan grant boshqa
     MTU ning omborida tugma chizmasligi kerak. Qaror baribir backendda. */
  const warehouse = layout.data?.warehouse;
  const target = {
    warehouseId,
    ...(warehouse === undefined ? {} : { mtuId: warehouse.mtuId, orgUnitId: warehouse.orgUnitId }),
  };
  const canEditLayout = can('warehouse.layout.edit', target);
  const canPrint = can('warehouse.label.print', target);

  const racks = useMemo<LayoutRack[]>(
    () => (layout.data?.zones ?? []).flatMap((zone) => zone.racks),
    [layout.data],
  );

  /* Tanlangan katakning javoni ko'rsatiladi; hech narsa tanlanmagan
     bo'lsa — oxirgi ochilgani, u ham bo'lmasa birinchisi. */
  const facadeRack = useMemo<LayoutRack | null>(() => {
    if (racks.length === 0) return null;
    const withSelected = racks.find((rack) => rack.cells.some((cell) => cell.id === cellId));
    return withSelected ?? racks.find((rack) => rack.id === rackId) ?? racks[0] ?? null;
  }, [racks, rackId, cellId]);

  /**
   * Qatorni qayta o'lchaydi.
   *
   * Qatorning UMUMIY uzunligi o'zgarmaydi — u javonning o'zi; o'zgaradigani
   * ichkaridagi o'rinlar. Kataklar qayta tug'ilgani uchun tanlov
   * TOZALANADI: eski `cellId` endi mavjud bo'lmasligi mumkin va u jimgina
   * xato katakka joylashtirishga olib borardi.
   */
  const splitRow = useMutation({
    mutationFn: async (partsCm: number[]) => {
      if (facadeRack === null || splitLevel === null) throw new Error('Qator tanlanmagan');
      const node = await warehouseApi.updateRack(facadeRack.id, {
        bays: applyLevel(facadeRack.bays, splitLevel, partsCm),
      });
      return node.cells.filter((cell) => cell.level === splitLevel).map((cell) => cell.id);
    },
    onSuccess: async (cellIds) => {
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      setSplitLevel(null);
      splitRow.reset();
      setCellId(null);
      setCode('');
      toast.show(t('warehouse.levelSplitDone'), 'success');
      /* Yangi o'rinlarning KODLARI o'zgardi — eski etiketka endi yolg'on
         gapiradi. Ruxsati bo'lsa darrov qayta bosish taklif qilinadi. */
      if (canPrint) setPrintCells(cellIds);
    },
  });

  const single = lines.length === 1;
  /* Ta'minotchisi ko'rsatilmagan satrlar — ularni skladchi o'zi
     to'ldiradi. Bitta maydon hammasiga: bir tanlovdagi satrlar bir
     yetkazmadan keladi, alohida-alohida so'rash esa ekranni maydon
     bilan to'ldirib tashlardi. */
  const needSupplier = lines.some((line) => line.supplier === null);
  const supplierOk = !needSupplier || supplier.trim().length >= 2;

  const amountOf = (line: SupplyReportLineDto): number => Number(qtyById[line.id] ?? '');
  const amountOk = (line: SupplyReportLineDto): boolean => {
    const amount = amountOf(line);
    return Number.isFinite(amount) && amount > 0 && amount <= line.toPlaceQty;
  };

  const ready = cellId !== null && supplierOk && lines.every(amountOk);

  const submit = (): void => {
    if (!ready || cellId === null) return;
    onAdd(
      lines.map((line) => ({
        lineId: line.id,
        cellId,
        qty: amountOf(line),
        ...(line.supplier === null ? { supplier: supplier.trim() } : {}),
      })),
    );
  };

  const first = lines[0];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        {/* Umumiy qaytish tugmasi — ombor sahifasidagi "Orqaga qaytish"
            bilan bir xil ko'k. `ghost` bu yerda xira matnga o'xshab qolardi. */}
        <BackButton onClick={onBack} className="self-start">
          {t('supply.backToDoc')}
        </BackButton>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-text-muted font-mono text-xs">{docNo}</span>
          {single && first !== undefined ? (
            <>
              <span className="text-text-muted text-xs">·</span>
              <span className="text-text-muted font-mono text-xs">{first.code}</span>
            </>
          ) : null}
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="text-text text-xl font-semibold tracking-tight">
            {single && first !== undefined
              ? first.name
              : t('supply.placeMany', { count: lines.length })}
          </h1>
          {single && first !== undefined ? (
            <span className="text-text-muted text-sm">
              {t('supply.remaining')}: <b className="tabular text-text">{first.toPlaceQty}</b>{' '}
              {first.unitRaw}
            </span>
          ) : null}
        </div>
      </header>

      <div className="grid gap-5 lg:grid-cols-[26rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-4">
            {/* Hujjatdan kelgan, O'ZGARMAYDIGAN ma'lumot — faqat bitta
                satr tanlanganda: ro'yxatda u har satrda boshqacha bo'ladi
                va jadvalda allaqachon ko'rinib turibdi. */}
            {single && first !== undefined ? (
              <dl className="border-border grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border p-3 text-xs">
                <dt className="text-text-muted">{t('supply.supplier')}</dt>
                <dd className="text-text">{first.supplier ?? '—'}</dd>
                <dt className="text-text-muted">{t('supply.arrivedAt')}</dt>
                <dd className="text-text tabular">{first.arrivedAt ?? '—'}</dd>
              </dl>
            ) : null}

            {needSupplier ? (
              <Input
                label={t('supply.supplierMissing')}
                hint={t('supply.supplierMissingHint')}
                value={supplier}
                onChange={(event) => setSupplier(event.target.value)}
                placeholder={t('supply.supplierPlaceholder')}
              />
            ) : null}

            <CellPicker
              warehouseId={warehouseId}
              value={code}
              onChange={(next) => {
                setCode(next);
                setCellId(null);
              }}
              onResolved={(cell) => {
                setCellId(cell?.id ?? null);
                if (cell !== null) qtyRef.current?.focus();
              }}
              onEnter={() => qtyRef.current?.focus()}
              inputRef={cellRef}
              {...(single && first?.nomenclatureId != null
                ? { nomenclatureId: first.nomenclatureId }
                : {})}
            />

            <div className="flex flex-col gap-3">
              {lines.map((line, index) => (
                <Input
                  key={line.id}
                  label={single ? t('supply.qty') : line.name}
                  hint={single ? undefined : `${line.code} · ${t('supply.remaining')}: ${line.toPlaceQty} ${line.unitRaw}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={line.toPlaceQty}
                  value={qtyById[line.id] ?? ''}
                  onChange={(event) =>
                    setQtyById((rows) => ({ ...rows, [line.id]: event.target.value }))
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') submit();
                  }}
                  {...(index === 0 ? { ref: qtyRef } : {})}
                />
              ))}
            </div>

            {cellId === null ? (
              <span className="text-text-muted text-xs">{t('supply.pickCellHint')}</span>
            ) : (
              <Badge tone="success">{t('supply.cellPicked', { code })}</Badge>
            )}

            <footer className="flex items-center justify-end gap-2">
              <Button variant="secondary" onClick={onBack}>
                {t('supply.cancel')}
              </Button>
              <Button onClick={submit} disabled={!ready}>
                <PackageCheck size={15} aria-hidden />
                {t('supply.addToBasket')}
              </Button>
            </footer>

            <p className={cn('text-text-muted text-xs', ready ? 'invisible' : '')}>
              {t('supply.readyHint')}
            </p>
          </Card>

          {basket}
        </div>

        <Card className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-text text-sm font-semibold">{t('supply.drawing')}</h2>
            <p className="text-text-muted text-xs">{t('supply.drawingHint')}</p>
          </div>
          {layout.isPending ? (
            <div className="text-text-muted text-sm">{t('supply.facadeLoading')}</div>
          ) : facadeRack === null ? (
            <div className="text-text-muted text-sm">{t('supply.facadeEmpty')}</div>
          ) : (
            <RackFacade
              rack={facadeRack}
              racks={racks}
              onSelectRack={(id) => {
                setRackId(id);
                // Boshqa javonga o'tilganda eski tanlov qolib ketmasin.
                setCellId(null);
                setCode('');
              }}
              filter="all"
              {...(canEditLayout ? { onSplitLevel: (level) => setSplitLevel(level) } : {})}
              pending={pendingByCell}
              {...(onRemoveFromCell === undefined
                ? {}
                : { onRemovePending: (id: string) => setRemoveCellId(id) })}
              selectedCellId={cellId}
              onSelectCell={(id) => {
                const cell = facadeRack.cells.find((c) => c.id === id);
                if (cell === undefined || cell.status === 'BLOCKED') return;
                setRackId(facadeRack.id);
                setCellId(id);
                setCode(cell.code);
                qtyRef.current?.focus();
              }}
            />
          )}
        </Card>
      </div>

      {/* Qatorni o'lchash oynasi — chizmadagi ruletkadan ochiladi. */}
      {facadeRack === null || !canEditLayout ? null : (
        <LevelSplitDialog
          open={splitLevel !== null}
          onClose={() => {
            setSplitLevel(null);
            splitRow.reset();
          }}
          rackCode={facadeRack.code}
          level={splitLevel ?? 1}
          currentM={facadeRack.bays[(splitLevel ?? 1) - 1] ?? []}
          onSubmit={(partsCm) => splitRow.mutate(partsCm)}
          pending={splitRow.isPending}
          error={
            splitRow.error instanceof ApiClientError
              ? splitRow.error.message
              : splitRow.error === null
                ? null
                : t('warehouse.levelSplitFailed')
          }
        />
      )}

      {/* Olib tashlashdan OLDIN so'raladi: × kichkina va katak ustida —
          tasodifan bosilib ketishi oson. */}
      {onRemoveFromCell === undefined ? null : (
        <ConfirmDialog
          open={removeCellId !== null}
          onClose={() => setRemoveCellId(null)}
          onConfirm={() => {
            if (removeCellId !== null) onRemoveFromCell(removeCellId);
            // Bo'shagan katak tanlangan bo'lib qolsa, keyingi joylashtirish
            // unga jimgina tushardi — tanlov ham tozalanadi.
            if (removeCellId === cellId) {
              setCellId(null);
              setCode('');
            }
            setRemoveCellId(null);
          }}
          tone="danger"
          title={t('supply.pendingRemoveTitle')}
          description={t('supply.pendingRemoveDesc', {
            code: racks.flatMap((rack) => rack.cells).find((c) => c.id === removeCellId)?.code ?? '',
            items: (pendingByCell.get(removeCellId ?? '') ?? [])
              .map((item) => `${item.name} (${item.qty})`)
              .join(', '),
          })}
          confirmLabel={t('supply.pendingRemoveConfirm')}
          cancelLabel={t('supply.cancel')}
        />
      )}

      {canPrint ? (
        <LabelPrintDialog
          open={printCells !== null}
          onClose={() => setPrintCells(null)}
          cellIds={printCells ?? []}
        />
      ) : null}
    </div>
  );
};
